import { useState, type ReactNode } from "react";
import { Card, Tabs, TabsContent, TabsList, TabsTrigger, cn } from "@nexus/ui";

import { WidgetHelp } from "../WidgetHelp/WidgetHelp";
import type { WidgetHelpTopic } from "../WidgetHelp/WidgetHelp";

export interface InsightTab {
  value: string;
  label: string;
  badge: number;
  /** Which explainer the help button opens while this panel is on screen. */
  help: WidgetHelpTopic;
  content: ReactNode;
}

export interface InsightTabsProps {
  /** Panels in display order; the first is open initially. */
  tabs: InsightTab[];
  className?: string;
}

/**
 * The tabbed shell both the report detail and the market dashboard put their insight panels in,
 * so a panel looks and behaves the same wherever it is read.
 *
 * The help button lives in the tab strip rather than inside each widget: the strip has the room
 * the panels do not, and one fixed position means users learn where the explanation is instead
 * of hunting for it per widget.
 */
export function InsightTabs({ tabs, className }: InsightTabsProps) {
  const [selected, setSelected] = useState(tabs[0]?.value);
  // A panel can vanish when its data does (scoping the hub drops the trends tab), which would
  // otherwise leave the strip pointing at a tab that no longer exists.
  const active = tabs.find((tab) => tab.value === selected) ?? tabs[0];

  if (!active) return null;

  return (
    <Card
      className={cn("border-border/60 gap-0 overflow-hidden p-0", className)}
    >
      <Tabs value={active.value} onValueChange={setSelected}>
        <div className="border-border flex items-center gap-1 border-b pr-2">
          {/* Horizontal scroll on narrow screens keeps the help button off a clipped tab.
              Pair with overflow-y-hidden: CSS turns a lone overflow-x:auto into overflow-y:auto
              too, which otherwise paints a vertical scrollbar inside the fixed-height strip. */}
          <TabsList
            variant="line"
            className="group-data-[orientation=horizontal]/tabs:h-11 min-w-0 flex-1 justify-start overflow-x-auto overflow-y-hidden border-b-0 px-3"
          >
            {tabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} badge={tab.badge}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <WidgetHelp topic={active.help} className="shrink-0" />
        </div>
        {tabs.map((tab) => (
          <TabsContent key={tab.value} value={tab.value} className="p-5">
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
}
